import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * WidgetRenderer - Sandboxed iframe for rendering AI-generated HTML/JS/SVG widgets.
 * Renders Chart.js charts, SVG diagrams, and styled HTML inline in chat messages.
 * Uses postMessage for auto-height adjustment.
 * Passes theme (dark/light) into iframe. Transparent bg on screen, white bg on export.
 */
const WidgetRenderer = React.memo(({ title, html }) => {
  const iframeRef = useRef(null);
  const widgetIdRef = useRef(`widget-${Math.random().toString(36).slice(2, 10)}`);
  const [height, setHeight] = useState(450);
  const [loaded, setLoaded] = useState(false);

  // Reactive theme from context — updates when user toggles
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Memoize enhanced HTML so iframe doesn't reload on parent re-renders
  const enhancedHtml = useMemo(() => {
    let result = html.replace(
      /window\.parent\.postMessage\(\s*\{\s*type:\s*'widget-resize'/g,
      `window.parent.postMessage({ type: 'widget-resize', id: '${widgetIdRef.current}'`
    );
    result = result.replace(/<html>/i, `<html class="${theme}">`);
    return result;
  }, [html, theme]);

  const handleMessage = useCallback((event) => {
    if (
      event.data?.type === 'widget-resize' &&
      event.data?.id === widgetIdRef.current &&
      typeof event.data?.height === 'number'
    ) {
      setHeight(event.data.height + 32);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const handleLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  // Download as PNG with white background (for formal documents)
  const handleDownload = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      // Create a new window with the widget HTML but forced white bg
      const exportHtml = html
        .replace(/background:\s*transparent/g, 'background: #fff')
        .replace(/<html[^>]*>/i, '<html>')
        .replace(
          /<body/i,
          '<body style="background:#fff;color:#111827"'
        );
      const blob = new Blob([exportHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) win.focus();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      console.error('Widget export failed:', e);
    }
  }, [html]);

  if (!html) return null;

  return (
    <div className="widget-renderer my-3" style={{
      borderRadius: '8px',
      overflow: 'hidden',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      position: 'relative'
    }}>
      {title && (
        <div style={{
          padding: '6px 12px',
          fontSize: '12px',
          fontWeight: 500,
          color: isDark ? '#9ca3af' : '#6b7280',
          background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{title}</span>
          <button
            onClick={handleDownload}
            title="Open in new tab (white background for export)"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 6px',
              borderRadius: '4px',
              color: isDark ? '#9ca3af' : '#6b7280',
              fontSize: '11px',
              opacity: 0.7
            }}
            onMouseEnter={e => e.target.style.opacity = 1}
            onMouseLeave={e => e.target.style.opacity = 0.7}
          >
            ↗ Export
          </button>
        </div>
      )}
      {!loaded && (
        <div style={{
          height: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9ca3af',
          fontSize: '13px'
        }}>
          Loading visualization...
        </div>
      )}
      <iframe
        ref={iframeRef}
        srcDoc={enhancedHtml}
        sandbox="allow-scripts"
        onLoad={handleLoad}
        style={{
          width: '100%',
          height: `${height}px`,
          border: 'none',
          background: 'transparent',
          display: loaded ? 'block' : 'none'
        }}
        title={title || 'Widget'}
      />
    </div>
  );
});

export default WidgetRenderer;
